import { AccountWithProjectsByDateDTO } from 'entities/account';

export function getKeysFromAccounts(accounts: AccountWithProjectsByDateDTO[]): Set<string> {
  const keys = new Set<string>();
  accounts.forEach((acc) => {
    keys.add(acc.id);
    acc.projects?.forEach((p) => keys.add(p.id));
  });
  return keys;
}

/**
 * Merge API accounts with grayed out checked items.
 * When searching, only returns API accounts.
 * When not searching, includes grayed out items that are still checked.
 */
export function mergeWithGrayedOut(
  apiAccounts: AccountWithProjectsByDateDTO[],
  grayedOutAccounts: AccountWithProjectsByDateDTO[],
  checkedKeys: string[],
  isSearching: boolean,
): AccountWithProjectsByDateDTO[] {
  if (isSearching) {
    return apiAccounts;
  }

  const result = [...apiAccounts];
  const apiAccountIds = new Set(apiAccounts.map((a) => a.id));

  grayedOutAccounts.forEach((grayedAcc) => {
    if (!apiAccountIds.has(grayedAcc.id)) {
      // Account not in API - add it if checked
      if (checkedKeys.includes(grayedAcc.id) || grayedAcc.projects.some((p) => checkedKeys.includes(p.id))) {
        result.push(grayedAcc);
      }
    } else {
      const apiAcc = apiAccounts.find((a) => a.id === grayedAcc.id);
      const apiProjectIds = new Set(apiAcc?.projects?.map((p) => p.id) || []);

      const missingProjects = grayedAcc.projects.filter((p) => !apiProjectIds.has(p.id) && checkedKeys.includes(p.id));

      if (missingProjects.length > 0) {
        const existingIdx = result.findIndex((a) => a.id === grayedAcc.id);
        if (existingIdx !== -1) {
          result[existingIdx] = {
            ...result[existingIdx],
            projects: [...result[existingIdx].projects, ...missingProjects],
          };
        }
      }
    }
  });

  return result;
}

/**
 * Find checked items from previous accounts that are no longer in new accounts.
 * These become "grayed out" items.
 */
export function findNewGrayedOut(
  prevAccounts: AccountWithProjectsByDateDTO[],
  newAccounts: AccountWithProjectsByDateDTO[],
  existingGrayedOut: AccountWithProjectsByDateDTO[],
  checkedKeys: string[],
): AccountWithProjectsByDateDTO[] {
  const newApiKeys = getKeysFromAccounts(newAccounts);
  const newGrayedOut: AccountWithProjectsByDateDTO[] = [];

  prevAccounts.forEach((prevAcc) => {
    if (!newApiKeys.has(prevAcc.id) && checkedKeys.includes(prevAcc.id)) {
      // Whole account is missing and was checked
      newGrayedOut.push(prevAcc);
    } else if (newApiKeys.has(prevAcc.id)) {
      // Account exists, check for missing projects
      const missingProjects = prevAcc.projects?.filter((p) => !newApiKeys.has(p.id) && checkedKeys.includes(p.id));
      if (missingProjects && missingProjects.length > 0) {
        newGrayedOut.push({ ...prevAcc, projects: missingProjects });
      }
    }
  });

  // Also keep existing grayed out items that are still checked
  existingGrayedOut.forEach((grayedAcc) => {
    const stillChecked =
      checkedKeys.includes(grayedAcc.id) || grayedAcc.projects.some((p) => checkedKeys.includes(p.id));
    if (stillChecked && !newApiKeys.has(grayedAcc.id)) {
      const existing = newGrayedOut.find((a) => a.id === grayedAcc.id);
      if (!existing) {
        newGrayedOut.push(grayedAcc);
      }
    }
  });

  return newGrayedOut;
}

/**
 * Remove a key (account or project) from grayed out accounts.
 * Used when user unchecks a grayed out item.
 */
export function removeKeyFromAccounts(
  accounts: AccountWithProjectsByDateDTO[],
  key: string,
): AccountWithProjectsByDateDTO[] {
  return accounts
    .map((acc) => {
      if (acc.id === key) return null;

      const hasMatchingProject = acc.projects.some((p) => p.id === key);
      if (!hasMatchingProject) {
        return acc;
      }

      const filteredProjects = acc.projects.filter((p) => p.id !== key);

      if (filteredProjects.length === 0) return null;

      return { ...acc, projects: filteredProjects };
    })
    .filter((a): a is AccountWithProjectsByDateDTO => a !== null);
}
